import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import cx from 'classnames';
import FlexBox from 'flexbox-react';
import { ThemeProvider } from 'react-css-themr';
import { Input, CardActions } from 'react-toolbox';
import { cloneDeep, omit, isEmpty } from 'lodash';
import Card from 'components/Card';
import ColoredButton from 'components/ColoredButton';
import styles from 'styles/form';
import RTCard from 'styles/card';
import RTInput from 'styles/input';
import RTDropdown from 'styles/dropdown';
import { PHASE_TYPES, MODE_OF_CONTACT, MILLWORK_SURFACES,
         EMAIL_VALIDATION_REGEX } from 'config/constants';
import { error } from '../../../../../../utils';
import { handleDateSave } from '../../../../../../utils/dateUtils';
import StyledSelect from 'components/StyledSelect';
import PhaseBasicInfoForm from './phaseBasicInfoForm';
import PhaseSurfaceInfoForm from './phaseSurfaceInfoForm';
import PhaseSpecificInfoForm from './phaseSpecificInfoForm';
import WorkorderInfoForm from './workorderInfoForm';

const theme = { RTInput, RTDropdown, RTCard };
const largeInput = cx(styles.inputs, styles.largeInput);
const cancelClass = cx([styles.cancelButton, styles.actionButton]);
const titleStyle = cx([styles.bottomTitle, styles.childTitle]);

const DEFAULT_PHONE_NUMBER = {
  number: '',
  kind: 'mobile',
  ok_to_text: false,
  reached: false,
};

const DEFAULT_PHASE_SPECIFICS = {
  customer_speaks_english: false,
  customer_speaks_spanish: false,
  customer_has_power: false,
  customer_has_water: false,
  customer_has_climate_control: false,
  need_move_furniture: false,
  need_move_furniture_oversized: false,
  need_move_appliances: false,
  need_subfloor_prep: false,
  need_subfloor_replacement: false,
  is_naildown_hardwood: false,
  is_gluedown_hardwood: false,
  is_floating_hardwood: false,
  is_cali_bamboo_hardwood: false,
  is_steps_hardwood: false,
  is_stretch_in_carpet: false,
  is_gluedown_carpet: false,
  is_binding_carpet: false,
  is_pattern_match_carpet: false,
  is_berber_carpet: false,
  is_steps_carpet: false,
  is_floor_tile: false,
  is_backsplash_tile: false,
  is_backsplash_glass: false,
  is_backsplash_blocking: false,
  is_patterns_tile: false,
  is_large_format_tile: false,
  is_vinyl_sheet: false,
  is_vct_lvt: false,
  need_remove_replace_toilet: false,
  need_remove_naildown_hardwood: false,
  need_remove_floating_hardwood: false,
  need_remove_gluedown_hardwood: false,
  need_remove_over_pad_carpet: false,
  need_remove_gluedown_carpet: false,
  need_remove_laminate: false,
  need_remove_tile: false,
  need_remove_gluedown_vinyl: false,
  need_remove_vinyl_plank: false,
  need_remove_backsplash: false,
  need_install_baseboard: false,
  need_hardsurface_transitions: false,
  millwork_specifics: { num_doors: 0, num_windows: 0, permit_required: false },
};

const LOCK_BLACKLISTED_ATTRIBUTES = ['summary', 'due_at'];

const renderTitle = (phase, isEdit) => <FlexBox className={styles.divider}>
  <div>
    <h5 className={styles.cardTitle}>{`${isEdit ? 'Edit' : 'Create'} Phase`}</h5>
    <p className={styles.cardSubtitle}>
      Please ensure that all fields contain accurate information
    </p>
  </div>
</FlexBox>;

class PhaseForm extends Component {
  constructor(props) {
    super(props);
    this.handleSave = this.handleSave.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSurfaceInfoChange = this.handleSurfaceInfoChange.bind(this);
    this.handlePhaseType = this.handleChange.bind(this, 'type');
    this.handleAdditionalComments = this.handleChange.bind(this, 'additional_comments');
    this.handleAdditionalWork = this.handleChange.bind(this, 'additional_work');
    this.handleHaulAway = this.handleChange.bind(this, 'haul_away');
    this.handleFWProjectId = this.handleChange.bind(this, 'floor_wizard_project_id');

    const editablePhase = cloneDeep(omit(this.props.phase, LOCK_BLACKLISTED_ATTRIBUTES));
    [0, 1, 2].forEach((i) => {
      if (!editablePhase.phone_numbers[i]) {
        editablePhase.phone_numbers[i] = cloneDeep(DEFAULT_PHONE_NUMBER);
      }
    });
    if (editablePhase.phase_specific === undefined) {
      editablePhase.phase_specific = DEFAULT_PHASE_SPECIFICS;
    } else if (!editablePhase.phase_specific.millwork_specifics) {
      editablePhase.phase_specific.millwork_specifics =
        { num_doors: 0, num_windows: 0, permit_required: false };
    }
    this.state = { phase: editablePhase, invalidEmail: false };
  }

  handleChange = (key, value, ...rest) => {
    const phase = this.state.phase;
    const emailChange = key === 'email';
    if (key === 'surface_type') {
      // When the surface types changes it should clear the old info
      phase.surface_information = {};
      phase.surface_type = {};
      phase.surface_type.id = value;
      phase.surface_type.name = rest[0];
    } else if (key === 'store') {
      const [vendor] = rest;
      const wasIfi = phase.store.vendor === 'IFI';
      phase.store.id = value;
      phase.store.vendor = vendor;
      if (wasIfi !== (vendor === 'IFI')) {
        // Crossing the IFI boundary invalidates the previously selected
        // surface type, same idiom as a direct surface_type change above.
        phase.surface_type = {};
        phase.surface_information = {};
      }
    } else if (key === 'phone_numbers') {
      const [index, attr, val] = [value, ...rest];
      phase.phone_numbers[index][attr] = val;
    } else if (key === 'floor_wizard_project_id') {
      phase.floor_wizard_job = phase.floor_wizard_job || {};
      phase.floor_wizard_job[key] = value;
    } else {
      phase[key] = emailChange ? value.replaceAll(' ', '') : value;
    }
    this.setState({ phase, invalidEmail: emailChange && !EMAIL_VALIDATION_REGEX.test(value) });
  }

  handleSpecificChange = (key, value) => {
    const phase = this.state.phase;
    if (key === 'need_move_furniture_oversized' && value) {
      phase.phase_specific.need_move_furniture = true;
    }
    phase.phase_specific[key] = value;
    this.setState({ phase });
  }

  handleMillworkSpecificChange = (key, value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return;
    const phase = { ...this.state.phase };
    phase.phase_specific = { ...phase.phase_specific };
    phase.phase_specific.millwork_specifics = {
      ...phase.phase_specific.millwork_specifics, [key]: num,
    };
    this.setState({ phase });
  }

  handleMillworkBooleanChange = (key, value) => {
    const phase = { ...this.state.phase };
    phase.phase_specific = { ...phase.phase_specific };
    phase.phase_specific.millwork_specifics = {
      ...phase.phase_specific.millwork_specifics, [key]: value,
    };
    this.setState({ phase });
  }

  handleSurfaceInfoChange = (key, value) => {
    const phase = this.state.phase;
    phase.surface_information = phase.surface_information || {};
    phase.surface_information[key] = value;
    this.setState({ phase });
  }

  handleCancel = () => this.props.onCancel();

  handleSave = (e) => {
    if (e) e.preventDefault();
    const { store, surface_type: surfaceType, phone_numbers, floor_wizard_job, ...phase } =
      this.state.phase;
    const phoneNumbersAttributes = phone_numbers.map((phone, i) => {
      if (phone.number.trim() === '') {
        if (!phone.id) return i > 0 ? null : phone;
        return { ...phone, _destroy: true };
      }
      return phone;
    }).filter(n => !!n);
    const data = {
      ...phase,
      phase_specific_attributes: phase.phase_specific,
      phone_numbers_attributes: phoneNumbersAttributes,
      floor_wizard_job_attributes: floor_wizard_job,
      store_id: store.id,
      surface_type_id: surfaceType ? surfaceType.id : null,
      date_sold: handleDateSave(phase.date_sold),
      vendor_eta: handleDateSave(phase.vendor_eta),
    };
    this.props.onSave(data);
  }

  render = () => {
    const { phase, invalidEmail } = this.state;
    const { errors } = this.props;
    const isEdit = !!phase.id;
    const role = this.props.role;
    const fwProjId = phase.floor_wizard_job ? phase.floor_wizard_job.floor_wizard_project_id : '';
    return (<ThemeProvider theme={theme}>
      <Card>
        <form onSubmit={this.handleSave} action="edit" method="POST">
          {renderTitle(phase, isEdit)}
          {!isEdit && <div className={styles.divider}>
            <h5 className={titleStyle}>Select a Phase Type</h5>
            <StyledSelect
              className={largeInput}
              label="Phase Type"
              onChange={this.handlePhaseType}
              options={
                PHASE_TYPES.map(pt => ({ value: pt, label: pt }))}
              value={phase.type}
              placeholder={'Select Phase Type'}
              error={error('type', errors)}
              required
            />
          </div>}
          {phase.type && phase.type === 'Workorder' && <WorkorderInfoForm
            phase={phase}
            errors={errors}
            theme={theme}
            onChange={this.handleChange}
          />}
          <PhaseBasicInfoForm
            phase={phase}
            errors={errors}
            theme={theme}
            onChange={this.handleChange}
            invalidEmail={invalidEmail}
            role={role}
          />
          {!!phase.surface_type && !isEmpty(phase.surface_type) && <Fragment>
            {!MILLWORK_SURFACES.includes(phase.surface_type.id) && <PhaseSurfaceInfoForm
              phase={phase}
              errors={errors}
              theme={theme}
              onChange={this.handleSurfaceInfoChange}
              onPhaseChange={this.handleChange}
            />}
            <PhaseSpecificInfoForm
              phase={phase}
              errors={errors}
              theme={theme}
              onChange={this.handleSpecificChange}
              onMillworkChange={this.handleMillworkSpecificChange}
              onMillworkBooleanChange={this.handleMillworkBooleanChange}
            />
          </Fragment>}
          {phase.type === 'Installation' && <div className={styles.divider}>
            <Input
              multiline
              label="Additional Work"
              theme={styles}
              value={phase.additional_work || ''}
              className={styles.textArea}
              error={error('additional_work', errors)}
              onChange={this.handleAdditionalWork}
            />
          </div>}
          {phase.type === 'Installation' && <div className={styles.divider}>
            <Input
              multiline
              label="Haul Away and Removal"
              theme={styles}
              value={phase.haul_away || ''}
              className={styles.textArea}
              error={error('haul_away', errors)}
              onChange={this.handleHaulAway}
            />
          </div>}
          <div className={styles.divider}>
            <Input
              multiline
              label="Additional comments"
              theme={styles}
              value={phase.additional_comments || ''}
              className={styles.textArea}
              error={error('additional_comments', errors)}
              onChange={this.handleAdditionalComments}
            />
            {phase.type === 'Assessment' && <Input
              label="Floor Wizard Project Id"
              value={fwProjId || ''}
              className={largeInput}
              onChange={this.handleFWProjectId}
              error={error('floor_wizard_job.floor_wizard_project_id', errors)}
            />}
          </div>
          <FlexBox justifyContent="flex-end" flexWrap="wrap">
            <CardActions className={styles.actionsWrapper}>
              <ColoredButton
                className={cancelClass}
                responsive
                color="gray"
                label="Cancel"
                onClick={this.handleCancel}
              />
              <ColoredButton
                type="submit"
                className={styles.actionButton}
                responsive
                color="green"
                label={isEdit ? 'Save Changes' : 'Create phase'}
                disabled={this.props.loading || invalidEmail}
              />
            </CardActions>
          </FlexBox>
        </form>
      </Card>
    </ThemeProvider>);
  }
}

PhaseForm.propTypes = {
  phase: PropTypes.object,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  errors: PropTypes.object,
  role: PropTypes.string,
  loading: PropTypes.bool,
};

PhaseForm.defaultProps = {
  phase: {
    phone_numbers: [],
    store: {},
    surface_type: {},
    mode_of_contact: MODE_OF_CONTACT[0],
    surface_information: {},
  },
};

export default PhaseForm;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseEdit/components/phaseForm.jsx